import { useEffect, useState, useCallback, useRef } from 'react';
import { mailGestaltung, type MailWerte, type Schriftzug } from '../api';

/**
 * Mail-Gestaltung für einen Kunden oder eine Instanz.
 *
 * ══ DAS LEITENDE PRINZIP ══════════════════════════════════════════════════
 *
 * Gespeichert wird nur, was **abweicht**. Ein Feld, das man nicht anfasst,
 * bleibt geerbt — von der Kundenebene oder der Vorgabe — und übernimmt damit
 * auch spätere Verbesserungen daran. Deshalb zeigt jedes Feld zwei Dinge: den
 * geltenden Wert (als Platzhalter) und, ob er hier gesetzt oder geerbt ist.
 *
 * Ohne diese Unterscheidung wäre nach dem ersten Speichern alles „gesetzt",
 * und die Vererbung stillschweigend tot.
 *
 * ══ ZUR VORSCHAU ══════════════════════════════════════════════════════════
 *
 * Sie rendert serverseitig mit demselben Code wie der echte Versand — nicht
 * mit einer Nachbildung im Browser. Eine Vorschau, die hübscher ist als die
 * Wirklichkeit, ist schlimmer als keine.
 */

type Ziel = { art: 'kunde' | 'instanz'; id: string };

const AUSRICHTUNGEN = [
  { wert: 'left', text: 'links' },
  { wert: 'center', text: 'zentriert' },
  { wert: 'right', text: 'rechts' },
] as const;

const SCHNITTE = [
  { wert: 400, text: 'normal' },
  { wert: 600, text: 'halbfett' },
  { wert: 700, text: 'fett' },
];

/** Einen Wert im verschachtelten Entwurf setzen bzw. bei `undefined` löschen. */
function setzen(o: Record<string, any>, pfad: string[], wert: unknown): Record<string, any> {
  const kopie = { ...o };
  let ziel = kopie;
  for (let i = 0; i < pfad.length - 1; i++) {
    ziel[pfad[i]] = { ...(ziel[pfad[i]] ?? {}) };
    ziel = ziel[pfad[i]];
  }
  const letzt = pfad[pfad.length - 1];
  if (wert === undefined || wert === '') delete ziel[letzt];
  else ziel[letzt] = wert;
  return kopie;
}

function lesen(o: Record<string, any> | undefined, pfad: string[]): unknown {
  let v: any = o;
  for (const p of pfad) {
    if (v === undefined || v === null) return undefined;
    v = v[p];
  }
  return v;
}

export default function MailGestaltungPanel({ ziel }: { ziel: Ziel }) {
  const [geltend, setGeltend] = useState<MailWerte | null>(null);
  const [entwurf, setEntwurf] = useState<Record<string, any>>({});
  const [absenderName, setAbsenderName] = useState<string | null>(null);
  const [logoQuelle, setLogoQuelle] = useState<string | null>(null);
  const [herkunft, setHerkunft] = useState<string[]>([]);
  const [vomKunden, setVomKunden] = useState<{ kundeName: string | null } | null>(null);
  const [vorschau, setVorschau] = useState<string>('');
  const [laedt, setLaedt] = useState(true);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>('ueberschriften');

  const holen = useCallback(async () => {
    setLaedt(true);
    try {
      const d = await mailGestaltung.lesen(ziel.art, ziel.id);
      setGeltend(d.geltend.werte);
      setHerkunft(d.geltend.herkunft);
      setEntwurf((d.eigene?.werte as Record<string, any>) ?? {});
      setAbsenderName(d.eigene?.absenderName ?? null);
      setLogoQuelle(d.eigene?.logoQuelle ?? null);
      setVomKunden(d.vomKunden ? { kundeName: d.vomKunden.kundeName } : null);
      setFehler(null);
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? 'Laden fehlgeschlagen');
    } finally {
      setLaedt(false);
    }
  }, [ziel.art, ziel.id]);

  useEffect(() => {
    holen();
  }, [holen]);

  // Die Vorschau folgt dem Entwurf, aber nicht bei jedem Tastendruck: eine
  // Abfrage je Zeichen wären bei einer Farbeingabe sechs Abrufe für einen Wert.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (laedt) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const d = await mailGestaltung.vorschau(ziel.art, ziel.id, {
          werte: entwurf,
          absenderName,
          logoQuelle,
        });
        setVorschau(d.html);
      } catch {
        /* Eine Vorschau, die kurz nicht kommt, ist kein Fehler zum Anschreien. */
      }
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [entwurf, absenderName, logoQuelle, laedt, ziel.art, ziel.id]);

  async function speichern() {
    try {
      await mailGestaltung.speichern(ziel.art, ziel.id, {
        werte: entwurf,
        absenderName,
        logoQuelle,
      });
      setMeldung('Gespeichert.');
      setTimeout(() => setMeldung(null), 2500);
      await holen();
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? 'Speichern fehlgeschlagen');
    }
  }

  async function zuruecksetzen() {
    if (!confirm('Alle hier gesetzten Werte verwerfen? Danach gilt wieder, was geerbt wird.'))
      return;
    try {
      await mailGestaltung.zuruecksetzen(ziel.art, ziel.id);
      setEntwurf({});
      setAbsenderName(null);
      setLogoQuelle(null);
      await holen();
    } catch (e: any) {
      setFehler(e?.response?.data?.error ?? 'Zurücksetzen fehlgeschlagen');
    }
  }

  if (laedt || !geltend) return <div className="text-sm text-slate-500">Wird geladen …</div>;

  const feld = 'rounded border border-slate-300 px-2 py-1 text-xs';

  /** Ein Eingabefeld, das den geerbten Wert als Platzhalter zeigt. */
  function Eingabe({
    pfad,
    art = 'text',
    breite = 'w-24',
    schritt,
  }: {
    pfad: string[];
    art?: 'text' | 'number' | 'color';
    breite?: string;
    schritt?: number;
  }) {
    const gesetzt = lesen(entwurf, pfad);
    const geerbt = lesen(geltend as unknown as Record<string, any>, pfad);
    const istGesetzt = gesetzt !== undefined;

    return (
      <span className="relative inline-flex items-center">
        <input
          type={art}
          step={schritt}
          value={(gesetzt ?? (art === 'color' ? (geerbt as string) ?? '#000000' : '')) as string}
          placeholder={geerbt !== undefined ? String(geerbt) : ''}
          onChange={(e) => {
            const roh = e.target.value;
            const wert =
              roh === ''
                ? undefined
                : art === 'number'
                  ? Number(roh)
                  : roh;
            setEntwurf((d) => setzen(d, pfad, wert));
          }}
          className={`${feld} ${breite} ${istGesetzt ? 'border-teal-400 bg-teal-50' : ''}`}
          title={istGesetzt ? 'Hier gesetzt — überschreibt das Geerbte' : 'Geerbt'}
        />
        {istGesetzt && (
          <button
            onClick={() => setEntwurf((d) => setzen(d, pfad, undefined))}
            className="ml-1 text-xs text-slate-400 hover:text-red-600"
            title="Auf geerbt zurücksetzen"
          >
            ↺
          </button>
        )}
      </span>
    );
  }

  function Auswahl({ pfad, optionen }: { pfad: string[]; optionen: ReadonlyArray<{ wert: string | number; text: string }> }) {
    const gesetzt = lesen(entwurf, pfad);
    const geerbt = lesen(geltend as unknown as Record<string, any>, pfad);
    return (
      <span className="inline-flex items-center">
        <select
          value={(gesetzt ?? '') as string}
          onChange={(e) => {
            const v = e.target.value;
            setEntwurf((d) =>
              setzen(d, pfad, v === '' ? undefined : typeof optionen[0].wert === 'number' ? Number(v) : v),
            );
          }}
          className={`${feld} w-28 ${gesetzt !== undefined ? 'border-teal-400 bg-teal-50' : ''}`}
        >
          <option value="">
            geerbt{geerbt !== undefined ? ` (${optionen.find((o) => o.wert === geerbt)?.text ?? geerbt})` : ''}
          </option>
          {optionen.map((o) => (
            <option key={String(o.wert)} value={String(o.wert)}>
              {o.text}
            </option>
          ))}
        </select>
      </span>
    );
  }

  function SchriftZeile({ titel, basis }: { titel: string; basis: string[] }) {
    return (
      <div className="flex flex-wrap items-center gap-1.5 py-1">
        <span className="w-28 shrink-0 text-xs text-slate-600">{titel}</span>
        <Eingabe pfad={[...basis, 'groesse']} art="number" breite="w-16" />
        <span className="text-xs text-slate-400">px</span>
        <Eingabe pfad={[...basis, 'farbe']} art="color" breite="w-12" />
        <Auswahl pfad={[...basis, 'schnitt']} optionen={SCHNITTE} />
        <Auswahl pfad={[...basis, 'ausrichtung']} optionen={AUSRICHTUNGEN} />
        <span className="text-xs text-slate-400">Abstand</span>
        <Eingabe pfad={[...basis, 'abstandOben']} art="number" breite="w-14" />
        <Eingabe pfad={[...basis, 'abstandUnten']} art="number" breite="w-14" />
      </div>
    );
  }

  const Abschnitt = ({ id, titel, kinder }: { id: string; titel: string; kinder: React.ReactNode }) => (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setOffen(offen === id ? null : id)}
        className="flex w-full items-center justify-between py-2 text-left text-xs font-semibold text-slate-700"
      >
        {titel}
        <span className="text-slate-400">{offen === id ? '▾' : '▸'}</span>
      </button>
      {offen === id && <div className="pb-3">{kinder}</div>}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        Gesetzt wird nur, was von der Vorgabe abweicht — alles andere wird geerbt und
        übernimmt spätere Änderungen daran. Geltend ist derzeit:{' '}
        <span className="font-medium">{herkunft.join(' → ')}</span>
        {vomKunden?.kundeName && ` (Kunde: ${vomKunden.kundeName})`}.
        {ziel.art === 'instanz' && (
          <> Was hier gesetzt wird, gilt nur für diese Instanz.</>
        )}
      </div>

      {fehler && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Einstellungen ──────────────────────────────────────────── */}
        <div className="rounded-md border border-slate-200 bg-white px-3">
          <Abschnitt
            id="absender"
            titel="Absender und Logo"
            kinder={
              <div className="space-y-2">
                <label className="block text-xs text-slate-600">
                  Absendername
                  <input
                    value={absenderName ?? ''}
                    onChange={(e) => setAbsenderName(e.target.value || null)}
                    placeholder="geerbt"
                    className={`mt-0.5 block w-full ${feld} ${absenderName ? 'border-teal-400 bg-teal-50' : ''}`}
                  />
                  <span className="mt-0.5 block text-slate-400">
                    Nur der angezeigte Name. Die Absenderadresse bleibt beim PC — eine
                    fremde Adresse im Absender lässt Mails im Spam landen.
                  </span>
                </label>

                <label className="block text-xs text-slate-600">
                  Logo (https-Adresse oder Bilddatei)
                  <input
                    value={logoQuelle?.startsWith('data:') ? '' : logoQuelle ?? ''}
                    onChange={(e) => setLogoQuelle(e.target.value || null)}
                    placeholder="https://…"
                    className={`mt-0.5 block w-full ${feld}`}
                  />
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 400_000) {
                        setFehler('Das Logo sollte unter 400 KB bleiben — große Bilder werden von Mailprogrammen abgeschnitten.');
                        return;
                      }
                      const leser = new FileReader();
                      leser.onload = () => setLogoQuelle(String(leser.result));
                      leser.readAsDataURL(f);
                    }}
                    className="mt-1 block w-full text-xs"
                  />
                  {logoQuelle?.startsWith('data:') && (
                    <span className="mt-0.5 flex items-center gap-2 text-teal-700">
                      Bilddatei hinterlegt
                      <button
                        onClick={() => setLogoQuelle(null)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        entfernen
                      </button>
                    </span>
                  )}
                </label>

                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Logogröße</span>
                  <Eingabe pfad={['logo', 'breite']} art="number" breite="w-16" />
                  <span className="text-xs text-slate-400">×</span>
                  <Eingabe pfad={['logo', 'hoehe']} art="number" breite="w-16" />
                  <Auswahl pfad={['logo', 'ausrichtung']} optionen={AUSRICHTUNGEN} />
                </div>
              </div>
            }
          />

          <Abschnitt
            id="ueberschriften"
            titel="Überschriften"
            kinder={
              <div>
                {(['h1', 'h2', 'h3', 'h4', 'h5'] as const).map((h) => (
                  <SchriftZeile key={h} titel={h.toUpperCase()} basis={['ueberschriften', h]} />
                ))}
                <p className="mt-1 text-xs text-slate-400">
                  H1 ist die Überschrift über dem Inhalt. H2–H5 greifen für Überschriften
                  im Text selbst.
                </p>
              </div>
            }
          />

          <Abschnitt
            id="text"
            titel="Fließtext und Inhalt"
            kinder={
              <div className="space-y-1">
                <SchriftZeile titel="Fließtext" basis={['fliesstext']} />
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Zeilenhöhe</span>
                  <Eingabe pfad={['fliesstext', 'zeilenhoehe']} art="number" breite="w-16" schritt={0.1} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Polster (o/u/s)</span>
                  <Eingabe pfad={['inhalt', 'polsterOben']} art="number" breite="w-14" />
                  <Eingabe pfad={['inhalt', 'polsterUnten']} art="number" breite="w-14" />
                  <Eingabe pfad={['inhalt', 'polsterSeitlich']} art="number" breite="w-14" />
                  <Auswahl pfad={['inhalt', 'ausrichtung']} optionen={AUSRICHTUNGEN} />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Schriftart</span>
                  <Eingabe pfad={['fliesstext', 'familie']} breite="w-56" />
                </div>
                <p className="text-xs text-slate-400">
                  Nur Schriften, die auf dem Gerät des Empfängers vorhanden sind —
                  Mailprogramme laden keine Webfonts. Arial, Helvetica, Georgia,
                  Verdana und Times sind überall da.
                </p>
              </div>
            }
          />

          <Abschnitt
            id="rahmen"
            titel="Hintergrund, Knopf und Akzent"
            kinder={
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Seite / Karte</span>
                  <Eingabe pfad={['hintergrund', 'seite']} art="color" breite="w-12" />
                  <Eingabe pfad={['hintergrund', 'karte']} art="color" breite="w-12" />
                  <span className="text-xs text-slate-400">Rahmen</span>
                  <Eingabe pfad={['hintergrund', 'rahmen']} art="color" breite="w-12" />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Ecken / Abstand</span>
                  <Eingabe pfad={['hintergrund', 'eckenRadius']} art="number" breite="w-14" />
                  <Eingabe pfad={['hintergrund', 'abstandAussen']} art="number" breite="w-14" />
                  <span className="text-xs text-slate-400">Breite</span>
                  <Eingabe pfad={['hintergrund', 'maxBreite']} art="number" breite="w-16" />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Knopf</span>
                  <Eingabe pfad={['knopf', 'hintergrund']} art="color" breite="w-12" />
                  <Eingabe pfad={['knopf', 'schrift']} art="color" breite="w-12" />
                  <span className="text-xs text-slate-400">Ecken</span>
                  <Eingabe pfad={['knopf', 'eckenRadius']} art="number" breite="w-14" />
                </div>
                <div className="flex flex-wrap items-center gap-1.5 py-1">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Akzentstrich</span>
                  <Eingabe pfad={['akzent', 'farbe']} art="color" breite="w-12" />
                  <Eingabe pfad={['akzent', 'strichBreite']} art="number" breite="w-14" />
                  <span className="text-xs text-slate-400">px (0 = aus)</span>
                </div>
              </div>
            }
          />

          <Abschnitt
            id="fuss"
            titel="Fußzeile und Rechtstexte"
            kinder={
              <div className="space-y-2">
                <label className="block text-xs text-slate-600">
                  Fußtext
                  <textarea
                    value={(lesen(entwurf, ['fusszeile', 'text']) as string) ?? ''}
                    onChange={(e) =>
                      setEntwurf((d) => setzen(d, ['fusszeile', 'text'], e.target.value || undefined))
                    }
                    placeholder={(lesen(geltend as any, ['fusszeile', 'text']) as string) ?? 'Diese Mail wurde automatisch verschickt …'}
                    rows={2}
                    className={`mt-0.5 block w-full ${feld}`}
                  />
                </label>
                <label className="block text-xs text-slate-600">
                  Rechtstext (Pflichtangaben)
                  <textarea
                    value={(lesen(entwurf, ['fusszeile', 'rechtstext']) as string) ?? ''}
                    onChange={(e) =>
                      setEntwurf((d) =>
                        setzen(d, ['fusszeile', 'rechtstext'], e.target.value || undefined),
                      )
                    }
                    placeholder="z. B. Firmierung, Registergericht, HRB, Geschäftsführung, USt-IdNr."
                    rows={3}
                    className={`mt-0.5 block w-full ${feld}`}
                  />
                </label>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="block text-xs text-slate-600">
                    Impressum-Adresse
                    <input
                      value={(lesen(entwurf, ['fusszeile', 'impressumUrl']) as string) ?? ''}
                      onChange={(e) =>
                        setEntwurf((d) =>
                          setzen(d, ['fusszeile', 'impressumUrl'], e.target.value || undefined),
                        )
                      }
                      placeholder="https://…/impressum"
                      className={`mt-0.5 block w-full ${feld}`}
                    />
                  </label>
                  <label className="block text-xs text-slate-600">
                    Datenschutz-Adresse
                    <input
                      value={(lesen(entwurf, ['fusszeile', 'datenschutzUrl']) as string) ?? ''}
                      onChange={(e) =>
                        setEntwurf((d) =>
                          setzen(d, ['fusszeile', 'datenschutzUrl'], e.target.value || undefined),
                        )
                      }
                      placeholder="https://…/datenschutz"
                      className={`mt-0.5 block w-full ${feld}`}
                    />
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="w-28 shrink-0 text-xs text-slate-600">Größe / Farbe</span>
                  <Eingabe pfad={['fusszeile', 'groesse']} art="number" breite="w-14" />
                  <Eingabe pfad={['fusszeile', 'farbe']} art="color" breite="w-12" />
                </div>
              </div>
            }
          />

          <div className="flex items-center gap-2 py-3">
            <button
              onClick={speichern}
              className="rounded bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800"
            >
              Speichern
            </button>
            <button
              onClick={zuruecksetzen}
              className="text-xs text-slate-600 hover:text-red-700"
            >
              Alles auf geerbt zurücksetzen
            </button>
            {meldung && <span className="text-xs text-teal-700">{meldung}</span>}
          </div>
        </div>

        {/* ── Vorschau ───────────────────────────────────────────────── */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">Vorschau</span>
            <span className="text-xs text-slate-400">
              serverseitig gerendert — genau wie beim Versand
            </span>
          </div>
          <iframe
            title="Mail-Vorschau"
            srcDoc={vorschau}
            // `sandbox` ohne allow-scripts: die Vorschau zeigt fremden Inhalt
            // (Rechtstexte, Logos), und der soll nichts ausführen können.
            sandbox=""
            className="h-[640px] w-full rounded-md border border-slate-200 bg-white"
          />
        </div>
      </div>
    </div>
  );
}
